import {Box, HStack, VStack} from 'platform/foundation';

import {clamp, range} from 'ramda';

import i18n from '@dms/i18n';
import {SkeletonLoadingBox} from '@dms/shared';

import {SKELETON_GEOMETRY} from '../../../constants/SKELETON_GEOMETRY';
import {useSkeletonRowCount} from '../../../hooks/useSkeletonRowCount';

const DEFAULT_VEHICLE_COLUMNS = 3;
const MAX_VEHICLE_COLUMNS = 6;
// Reserve space for the photo and title, as in the loaded comparison matrix.
const LABEL_HEADER_PADDING = 60;

interface ComparisonSkeletonProps {
  vehicleCount?: number;
}

export function ComparisonSkeleton(props: ComparisonSkeletonProps) {
  const rowCount = useSkeletonRowCount(SKELETON_GEOMETRY.tableRowMinHeightPx);
  const columnCount = clamp(1, MAX_VEHICLE_COLUMNS, props.vehicleCount ?? DEFAULT_VEHICLE_COLUMNS);

  return (
    <Box height="100%" overflow="hidden">
      <div role="status" aria-label={i18n.t('general.labels.loading')}>
        <HStack>
          <Box width={40} flexShrink={0} padding={4} paddingTop={LABEL_HEADER_PADDING}>
            <VStack spacing={6}>
              {range(0, rowCount).map((row) => (
                <SkeletonLoadingBox
                  key={row}
                  isLoading
                  width={SKELETON_GEOMETRY.comparisonWideCellWidth}
                  height={3}
                />
              ))}
            </VStack>
          </Box>
          {range(0, columnCount).map((column) => (
            <Box
              key={column}
              width={40}
              flexShrink={0}
              backgroundColor="general.white"
              borderRight="1px solid"
              borderColor="general.separator"
              paddingTop={6}
            >
              <VStack spacing={4}>
                <SkeletonLoadingBox isLoading height={30} />
                <Box padding={2}>
                  <VStack spacing={4}>
                    <SkeletonLoadingBox isLoading height={5} />
                    <SkeletonLoadingBox
                      isLoading
                      height={3}
                      width={SKELETON_GEOMETRY.comparisonWideCellWidth}
                    />
                    <SkeletonLoadingBox isLoading height={6} />
                    {range(0, rowCount).map((row) => (
                      <Box
                        key={row}
                        paddingVertical={2}
                        borderBottom="1px solid"
                        borderColor="general.separator"
                      >
                        <SkeletonLoadingBox
                          isLoading
                          width={
                            column % 2 === 0
                              ? SKELETON_GEOMETRY.comparisonWideCellWidth
                              : SKELETON_GEOMETRY.comparisonNarrowCellWidth
                          }
                          height={3}
                        />
                      </Box>
                    ))}
                  </VStack>
                </Box>
              </VStack>
            </Box>
          ))}
        </HStack>
      </div>
    </Box>
  );
}
